import { PERMISSION_SUBJECTS, type PermissionSubjects } from '@/helpers/casl/ability';
import { useIsOfflineConnectivity } from '@/hooks/desktop/useIsOfflineConnectivity';
import { isSessionNotFoundError } from '@/lib/SessionRecordingProvider/errors';
import { logger } from '@/utils/logger/logger';
import { isOfflineLocalSession } from '@/utils/offline-recording/localSessionSettings';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { getSessionPermissions, notFoundSessionIds } from '@heidi/session/data';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useMemo } from 'react';

import { useQueryJoinedTeams } from './query/useQueryJoinedTeams';
import { useRBACPermissionCheck } from './useRBACPermissionCheck';

// The key + wire type moved to `@heidi/session/contracts`, and the inline
// permissions GET became the carved `getSessionPermissions` (D3); this hook
// (the casl/offline/joined-teams composition) stays app-side.
import {
  GET_SESSION_BY_ID,
  type QuerySessionResponse,
  SESSION_PERMISSIONS_QUERY_KEY,
} from '@heidi/session/contracts';

export { SESSION_PERMISSIONS_QUERY_KEY };
const loggedPermissionMismatchesBySession = new Map<string, Set<string>>();

export function resetMismatchCache() {
  loggedPermissionMismatchesBySession.clear();
}

const getPermissionMismatchKey = (
  teamOthersSessionPermission: PermissionSubjects,
  sessionPermission: PermissionSubjects,
  apiResult: boolean,
  rbacResult: boolean
) =>
  [
    teamOthersSessionPermission,
    sessionPermission,
    String(apiResult),
    String(rbacResult),
  ].join('|');

export const useSessionPermissionCheck = (sessionId: string) => {
  const { checkRBACPermission } = useRBACPermissionCheck();
  const { data: joinedTeamsData } = useQueryJoinedTeams();
  const hasTeam = (joinedTeamsData?.joined_teams?.length ?? 0) > 0;

  // Offline local sessions have no server record — skip the permissions API and
  // fall back to the RBAC result so controls aren't disabled while wifi is off.
  const sessionData = reactQueryClient.getQueryData<QuerySessionResponse>([
    GET_SESSION_BY_ID,
    { sessionId },
  ]);
  // Treat the session as offline either when it's a local-only record or when
  // the app itself is offline. Otherwise the permissions API can't be reached
  // and every permission-gated control (e.g. the note template selector) gets
  // denied; falling back to the RBAC result keeps them usable while wifi is off.
  const isOffline = useIsOfflineConnectivity();
  const isOfflineSession = isOfflineLocalSession(sessionData) || isOffline;

  const {
    data: permissionCodes,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [SESSION_PERMISSIONS_QUERY_KEY, sessionId],
    enabled: !!sessionId && !notFoundSessionIds.has(sessionId) && !isOfflineSession,
    // Sit just below the BE Redis TTL of 90s (session_permission_manager.py)
    // so refetches still hit warm cache while reducing per-JWT request rate.
    staleTime: 75 * 1000,
    refetchOnWindowFocus: false,
    retry: (failureCount, err) =>
      isAxiosError(err) && err.response?.status === 429 ? false : failureCount < 1,
    queryFn: async () => {
      try {
        const { data } = await getSessionPermissions(sessionId);
        return data?.session_permissions ?? [];
      } catch (err) {
        if (isSessionNotFoundError(err)) {
          logger.warn(
            'Session not found when fetching permissions, session may have been deleted',
            { sessionId }
          );
          return null;
        }
        throw err;
      }
    },
  });

  useEffect(() => {
    if (isError) {
      logger.error('Failed to fetch session permissions', error);
    }
  }, [isError, error]);

  const permissionSet = useMemo(() => new Set(permissionCodes), [permissionCodes]);

  /**
   * Check if the user has a given permission for this session.
   */
  const checkPermission = useCallback(
    (
      teamOthersSessionPermission: PermissionSubjects,
      sessionPermission: PermissionSubjects
    ): boolean => {
      // Users not in a team cannot share sessions — hide sharing UI
      if (!hasTeam && sessionPermission === PERMISSION_SUBJECTS.SESSION_SHARE) {
        return false;
      }

      const rbacResult = checkRBACPermission(
        teamOthersSessionPermission,
        sessionPermission
      );

      if (isOfflineSession) {
        return rbacResult;
      }

      if (isLoading || isError || !permissionCodes) {
        return false;
      }

      const apiResult =
        permissionSet.has(teamOthersSessionPermission) ||
        permissionSet.has(sessionPermission);

      // log permission mismatch between API and RBAC without repeating a bunch of same messages
      if (apiResult !== rbacResult && sessionId) {
        const mismatchKey = getPermissionMismatchKey(
          teamOthersSessionPermission,
          sessionPermission,
          apiResult,
          rbacResult
        );
        const loggedMismatchesForSession =
          loggedPermissionMismatchesBySession.get(sessionId) ?? new Set<string>();

        if (!loggedPermissionMismatchesBySession.has(sessionId)) {
          loggedPermissionMismatchesBySession.set(sessionId, loggedMismatchesForSession);
        }

        if (loggedMismatchesForSession.has(mismatchKey)) {
          return apiResult;
        }

        loggedMismatchesForSession.add(mismatchKey);
        logger.warn('Session permission mismatch between API and RBAC', {
          teamOthersSessionPermission,
          sessionPermission,
          apiResult,
          rbacResult,
        });
      }

      return apiResult;
    },
    [
      isOfflineSession,
      hasTeam,
      isLoading,
      isError,
      sessionId,
      permissionCodes,
      permissionSet,
      checkRBACPermission,
    ]
  );

  return { checkPermission };
};

export function invalidateSessionPermissions(sessionId: string) {
  void reactQueryClient.invalidateQueries({
    queryKey: [SESSION_PERMISSIONS_QUERY_KEY, sessionId],
  });
}
